import { useSyncExternalStore } from 'react';

/**
 * Toasts after writes (D35): the outcome of saving or deleting data, shown for a while in the corner of the screen.
 * A small store outside React, so that the mutation cache (error toasts of every failed write) can show them too;
 * rendered and announced by {@link Toaster}.
 */
export type ToastType = 'success' | 'error';

export interface Toast {
  id: number;
  type: ToastType;
  text: string;
}

/** How long a toast stays (ms); the time stops while the pointer is over the toasts, focus is in them or the tab is hidden. */
export const TOAST_DURATION: Record<ToastType, number> = { success: 6000, error: 10000 };
/** Older toasts give way to newer ones. */
export const MAX_TOASTS = 3;

let current: Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function set(next: Toast[]) {
  current = next;
  listeners.forEach((listener) => listener());
}

function show(type: ToastType, text: string): number {
  const id = nextId++;
  set([...current.filter((t) => t.text !== text || t.type !== type), { id, type, text }].slice(-MAX_TOASTS));
  return id;
}

export const toast = {
  success: (text: string) => show('success', text),
  error: (text: string) => show('error', text),
  dismiss: (id: number) => set(current.filter((t) => t.id !== id)),
  /** Removes every toast (tests, sign-out). */
  clear: () => set([]),
};

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useToasts(): Toast[] {
  return useSyncExternalStore(subscribe, () => current);
}
